import React from "react";
import {
  Users,
  BookOpen,
  Layers,
  Calendar,
  FileCheck,
  Workflow,
  Sliders,
  Eye,
  ChevronRight,
  GraduationCap,
  Building2,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthContext";
import { userService } from "../../../services/userService";
import { programService } from "../../../services/programService";
import { hierarchyService } from "../../../services/hierarchyService";
import AdminLayout from "../../../components/layouts/AdminLayout";
import ResourceServiceBooking from "../../resource-service-booking";
import FormativeAssessmentManagement from "../../formative-assessment-management";

const AdminDashboard = ({ setViewRole, initialWorkspace = null }) => {
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = React.useState({
    userCounts: { total: 0, students: 0, academic: 0, staff: 0 },
    activePrograms: 0,
    departmentCount: 0,
    hierarchy: { total: 0, faculties: 0, departments: 0, sections: 0 },
    loading: true,
    error: false,
  });
  const [showSpaceBooking, setShowSpaceBooking] = React.useState(false);
  const [showFormativeAssessments, setShowFormativeAssessments] =
    React.useState(initialWorkspace === "formative_assessments");

  React.useEffect(() => {
    const fetchStats = async () => {
      try {
        const [userCounts, programStats, hierarchyStats] = await Promise.all([
          userService.getCategorizedUserCounts(),
          programService.getStats(),
          hierarchyService.getStats(),
        ]);

        setStats({
          userCounts: userCounts,
          activePrograms: programStats.activePrograms,
          departmentCount: programStats.departments,
          hierarchy: {
            total:
              hierarchyStats.faculties +
              hierarchyStats.departments +
              hierarchyStats.sections,
            ...hierarchyStats,
          },
          loading: false,
          error: false,
        });
      } catch (error) {
        console.error("Failed to fetch admin stats:", error);
        setStats((prev) => ({ ...prev, loading: false, error: true }));
      }
    };

    fetchStats();
  }, []);

  const fullName =
    profile?.full_name || user?.user_metadata?.full_name || "Administrator";

  const openOfficerView = (workspace) => {
    const roleByWorkspace = {
      hostel: "hostel_officer",
      transport: "transport_officer",
      estates: "estates_officer",
    };
    if (setViewRole && roleByWorkspace[workspace]) {
      // Keep the administrator at the root dashboard URL while switching the
      // visible workspace in-place. The dedicated route remains available
      // for direct links and refreshes.
      setViewRole(roleByWorkspace[workspace]);
      return;
    }
    navigate(
      `/non-teaching-dashboard?workspace=${workspace}&view=overview&viewAs=${roleByWorkspace[workspace]}`,
    );
  };

  const quickActions = [
    {
      label: "User Management",
      desc: "Accounts & role assignments",
      href: "/enhanced-user-management-system",
      icon: Users,
      color: "text-blue-600 bg-blue-50 border-blue-100",
    },
    {
      label: "Programs & Units",
      desc: "Curriculum & unit offerings",
      href: "/academic-management",
      icon: BookOpen,
      color: "text-emerald-600 bg-emerald-50 border-emerald-100",
    },
    {
      label: "Institution Hierarchy",
      desc: "Faculties, departments & sections",
      href: "/institution-hierarchy-management",
      icon: Layers,
      color: "text-purple-600 bg-purple-50 border-purple-100",
    },
    {
      label: "Formative Assessments",
      desc: "CAM marks entry & marksheets",
      href: "/formative-assessment-management",
      icon: FileCheck,
      color: "text-amber-600 bg-amber-50 border-amber-100",
    },
    {
      label: "Summative Assessment",
      desc: "Exam marks, approvals & results",
      href: "/summative-assessment-portal",
      icon: GraduationCap,
      color: "text-orange-600 bg-orange-50 border-orange-100",
    },
    {
      label: "Summative Timetable",
      desc: "Generate theory & practical exam slots",
      href: "/summative-assessment-schedule-management",
      icon: Calendar,
      color: "text-violet-600 bg-violet-50 border-violet-100",
    },
    {
      label: "Timetable Management",
      desc: "Master schedules & room allocation",
      href: "/timetable-management",
      icon: Calendar,
      color: "text-indigo-600 bg-indigo-50 border-indigo-100",
    },
    {
      label: "Workflows & Approvals",
      desc: "Administrative routing center",
      href: "/administrative-workflow-center",
      icon: Workflow,
      color: "text-cyan-600 bg-cyan-50 border-cyan-100",
    },
    {
      label: "Services & Operations",
      desc: "Hostel, transport and campus services",
      href: "/non-teaching-dashboard?workspace=hostel&view=overview",
      icon: Building2,
      color: "text-teal-600 bg-teal-50 border-teal-100",
    },
    {
      label: "Book a space",
      desc: "Request an available room or boardroom",
      href: "/resource-service-booking",
      icon: Building2,
      color: "text-blue-600 bg-blue-50 border-blue-100",
    },
    {
      label: "System Settings",
      desc: "Institution branding & parameters",
      href: "/system-settings",
      icon: Sliders,
      color: "text-rose-600 bg-rose-50 border-rose-100",
    },
  ];

  return (
    <AdminLayout
      title="Admin Overview"
      onOpenFormative={() => setShowFormativeAssessments(true)}
      onOpenService={openOfficerView}
    >
      {showFormativeAssessments ? (
        <div className="space-y-4">
          <button
            type="button"
            onClick={() => setShowFormativeAssessments(false)}
            className="text-sm font-semibold text-blue-700 hover:underline"
          >
            ← Back to admin dashboard
          </button>
          <FormativeAssessmentManagement />
        </div>
      ) : showSpaceBooking ? (
        <ResourceServiceBooking
          embedded
          onBack={() => setShowSpaceBooking(false)}
        />
      ) : <div className="space-y-6">
        <section className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-sm text-blue-700">Administration workspace</p>
            <h1 className="text-3xl font-bold text-slate-950">
              Welcome, {fullName}
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              System administration and institutional controls
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-1 rounded-xl border bg-white p-1.5 shadow-sm">
            <span className="flex items-center gap-1 px-2 text-xs font-semibold text-slate-500">
              <Eye className="h-3.5 w-3.5" /> View as
            </span>
            <button
              onClick={() => setViewRole("staff")}
              className="rounded-lg px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100"
            >
              Staff
            </button>
            <button
              onClick={() => setViewRole("student")}
              className="rounded-lg px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100"
            >
              Student
            </button>
            <button
              onClick={() => setViewRole("chief_principal")}
              className="rounded-lg px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100"
            >
              Chief Principal
            </button>
            <button
              onClick={() => setViewRole("vice_chancellor")}
              className="rounded-lg px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100"
            >
              Vice-Chancellor
            </button>
            <button
              onClick={() => openOfficerView("hostel")}
              className="rounded-lg px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100"
            >
              Hostel
            </button>
            <button
              onClick={() => openOfficerView("transport")}
              className="rounded-lg px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100"
            >
              Transport
            </button>
            <button
              onClick={() => openOfficerView("estates")}
              className="rounded-lg px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100"
            >
              Estates
            </button>
          </div>
        </section>

        <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {[
            [
              "System users",
              stats.userCounts.total,
              Users,
              "bg-blue-100 text-blue-700",
            ],
            [
              "Students",
              stats.userCounts.students,
              GraduationCap,
              "bg-indigo-100 text-indigo-700",
            ],
            [
              "Active programmes",
              stats.activePrograms,
              BookOpen,
              "bg-emerald-100 text-emerald-700",
            ],
            [
              "Departments",
              stats.departmentCount,
              Building2,
              "bg-purple-100 text-purple-700",
            ],
          ].map(([label, count, Icon, tone]) => (
            <div
              key={label}
              className="flex items-center gap-4 rounded-xl border bg-white p-5 shadow-sm"
            >
              <span
                className={`grid h-12 w-12 shrink-0 place-items-center rounded-xl ${tone}`}
              >
                <Icon className="h-6 w-6" />
              </span>
              <span>
                <strong className="block text-2xl text-slate-950">
                  {stats.loading
                    ? "…"
                    : stats.error
                      ? "—"
                      : Number(count || 0).toLocaleString()}
                </strong>
                <small className="text-slate-500">{label}</small>
              </span>
            </div>
          ))}
        </section>

        <section className="grid gap-6 lg:grid-cols-[1fr_360px]">
          <div className="rounded-xl border bg-white p-6 shadow-sm">
            <h2 className="text-xl font-bold text-slate-950">
              Quick operations
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              Open the main institutional administration modules.
            </p>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {quickActions.map((act) => (
                <Link
                  key={act.label}
                  to={act.href}
                  onClick={(event) => {
                    if (act.href === "/resource-service-booking") {
                      event.preventDefault();
                      setShowSpaceBooking(true);
                    }
                    if (act.href === "/formative-assessment-management") {
                      event.preventDefault();
                      setShowFormativeAssessments(true);
                    }
                  }}
                  className="group flex items-center gap-3 rounded-lg border p-4 text-left hover:bg-slate-50"
                >
                  <span
                    className={`grid h-9 w-9 shrink-0 place-items-center rounded-lg border ${act.color}`}
                  >
                    <act.icon className="h-4 w-4" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <strong className="block text-sm font-semibold text-slate-900">
                      {act.label}
                    </strong>
                    <small className="mt-0.5 block truncate text-xs text-slate-500">
                      {act.desc}
                    </small>
                  </span>
                  <ChevronRight className="h-4 w-4 shrink-0 text-slate-400 transition-transform group-hover:translate-x-0.5 group-hover:text-slate-700" />
                </Link>
              ))}
            </div>
          </div>

          <aside className="rounded-xl border bg-white p-6 shadow-sm">
            <h2 className="text-lg font-bold text-slate-950">
              System overview
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              Current institutional records
            </p>
            <dl className="mt-4 space-y-3">
              {[
                ["Academic staff", stats.userCounts.academic],
                ["Other staff", stats.userCounts.staff],
                ["Faculties", stats.hierarchy.faculties],
                ["Sections", stats.hierarchy.sections],
              ].map(([label, count]) => (
                <div
                  key={label}
                  className="flex items-center justify-between border-b pb-3 last:border-0"
                >
                  <dt className="text-sm text-slate-500">{label}</dt>
                  <dd className="font-mono text-sm font-bold text-slate-900">
                    {stats.loading
                      ? "…"
                      : stats.error
                        ? "—"
                        : Number(count || 0).toLocaleString()}
                  </dd>
                </div>
              ))}
            </dl>
          </aside>
        </section>
      </div>}
    </AdminLayout>
  );
};

export default AdminDashboard;
